<script setup lang="ts">
import { useI18n } from '#imports'

const { t } = useI18n()
const display = useDisplay()
const router = useRouter()
const userStore = useUserStore()
const systemStore = useSystemStore()

const searchRef = ref()

// 모바일 검색창에서 Enter 입력 시 검색 결과 페이지로 이동
function submitSearch() {
  const keyword = (systemStore.search || '').trim()
  if (!keyword)
    return
  systemStore.clientDrawerRight = false
  router.push({ path: '/search', query: { q: keyword } })
}

const loginActions = ref([
  { title: '마이페이지', icon: 'mdi-account-circle', color: 'black', variant: 'text' },
  { title: '알림', icon: 'mdi-bell', color: 'black', variant: 'text' },
  { title: '로그아웃', icon: 'mdi-logout', color: 'black', variant: 'text' },
])

const logoutActions = ref([
  { title: t('navigation.login'), icon: 'mdi-login', color: 'black', variant: 'text' },
  { title: t('navigation.register'), icon: 'mdi-account-plus', color: 'black', variant: 'text' },
])

// 사용자 타입에 따른 동적 버튼 텍스트
const campaignButtonText = computed(() => {
  if (!userStore.userStoreData || !userStore.userStoreData.userType)
    return ''

  const userType = userStore.userStoreData.userType || userStore.userStoreData.type

  if (userType === 'creator') {
    return '캠페인 보기'
  }
  else if (userType === 'advertiser') {
    return '크리에이터 보기'
  }
  return ''
})

// 캠페인/크리에이터 보기 버튼 클릭 핸들러
function handleCampaignButton() {
  systemStore.clientDrawerRight = false

  if (!userStore.userStoreData || !userStore.userStoreData.userType)
    return

  const userType = userStore.userStoreData.userType || userStore.userStoreData.type

  if (userType === 'creator') {
    router.push('/creator/campaigns')
  }
  else if (userType === 'advertiser') {
    router.push('/advertiser/campaigns')
  }
}

function handleMenuClick(title: string) {
  systemStore.clientDrawerRight = false

  switch (title) {
    case t('navigation.creator'):
      router.push('/creator')
      break
    case t('navigation.brand'):
      router.push('/advertiser')
      break
    case t('navigation.inquiry'):
      router.push('/inquiry')
      break
    case t('navigation.portfolio'):
      router.push('/portfolio')
      break
    case t('navigation.about'):
      router.push('/introduce')
      break
  }
}

function handleLoginActionClick(title: string) {
  systemStore.clientDrawerRight = false

  switch (title) {
    case '마이페이지':
      router.push('/myHome')
      break
    case '알림':
      // 알림 기능 추가 예정
      break
    case '로그아웃':
      // 로그아웃 처리
      userStore.userStoreData = null
      userStore.$reset() // Pinia store 완전 초기화
      router.push('/')
      break
    case t('navigation.login'):
      router.push('/user/login')
      break
    case t('navigation.register'):
      router.push('/user/register')
      break
  }
}
</script>

<template>
  <v-navigation-drawer
    v-if="!display.smAndUp.value"
    v-model="systemStore.clientDrawerRight"
    location="right"
    temporary
    width="300"
    style="background: #ffffff; backdrop-filter: blur(12px); z-index: 9999 !important;"
  >
    <template #prepend>
      <v-container class="d-flex align-center justify-space-between py-0 pl-3 pr-0 flex-row">
        <SiteTranslateWidget inline />
        <v-btn
          size="x-large" icon="mdi-close" variant="text" color="black"
          @click="systemStore.clientDrawerRight = false"
        />
      </v-container>
      <v-container class="d-flex align-center flex-column ma-0 px-4 py-0">
        <v-text-field
          ref="searchRef" v-model="systemStore.search" color="black"
          min-width="100%" density="compact" hide-details class="mx-1" rounded="pill"
          variant="outlined" bg-color="transparent"
          @keyup.enter="submitSearch"
        >
          <template #append-inner>
            <v-icon color="black" @click="submitSearch">
              mdi-magnify
            </v-icon>
          </template>
        </v-text-field>
        <v-container class="w-100" fluid>
          <!-- 로그인 안 된 상태 -->
          <v-row
            v-if="!userStore.userStoreData || !userStore.userStoreData.userType"
            class="align-center w-100 justify-center"
            no-gutters
          >
            <v-col
              v-for="(action, aIdx) in logoutActions"
              :key="action.title"
              class="d-flex align-center flex-row justify-center"
              cols="auto"
            >
              <v-btn
                class="font-weight-semibold"
                :color="action.color"
                variant="text"
                width="fit-content"
                @click="handleLoginActionClick(action.title)"
              >
                {{ action?.title }}
              </v-btn>

              <v-divider
                v-if="aIdx < logoutActions.length - 1"
                vertical
                class="mx-2 border-opacity-100"
                color="black"
                style="min-height: 16px;"
              />
            </v-col>
          </v-row>

          <!-- 로그인 된 상태 -->
          <v-row
            v-else-if="userStore.userStoreData && userStore.userStoreData.userType"
            class="align-center w-100 justify-end"
            no-gutters
          >
            <v-col
              v-for="action in loginActions"
              :key="action.title"
              class="d-flex justify-end"
              cols="auto"
            >
              <v-btn
                class="font-weight-medium text-caption mx-1 mr-4"
                width="fit-content"
                :color="action.color"
                variant="text"
                size="small"
                @click="handleLoginActionClick(action.title)"
              >
                {{ action?.title }}
              </v-btn>
            </v-col>

            <!-- 캠페인/크리에이터 보기 버튼 -->
            <v-col
              v-if="campaignButtonText"
              class="d-flex mt-3 w-100 justify-center"
              cols="12"
            >
              <v-btn
                color="c2"
                variant="flat"
                rounded="pill"
                style="color: black !important;"
                @click="handleCampaignButton"
              >
                {{ campaignButtonText }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-container>
    </template>
    <v-list nav>
      <v-list-item
        v-for="menu in systemStore.clientDrawerRightMenuList"
        :key="menu.title"
        class="text-start"
        @click="handleMenuClick(menu.title)"
      >
        <p class="font-pretendard font-weight-bold text-size">
          {{ menu.title }}
        </p>
      </v-list-item>
    </v-list>
    <!-- Language Selector -->
    <!-- <template #append>
            <v-container class="d-flex align-center justify-end">

                <v-menu offset-y>
                    <template #activator="{ props }">
                        <v-btn v-bind="props" :text="currentLocale?.name" prepend-icon="mdi-translate" variant="text"
                            color="black" class="font-weight-medium text-subtitle-2 font-pretendard" size="small"
                             />
                    </template>
                    <v-list>
                        <v-list-item v-for="availableLocale in locales" :key="availableLocale.code"
                            @click="setLocale(availableLocale.code); systemStore.clientDrawerRight = false">
                            <v-list-item-title>{{ availableLocale.name }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </v-container>
        </template> -->
  </v-navigation-drawer>
</template>

<style scoped>
/* 모바일 네비게이션 드로어 최적화 */
.v-navigation-drawer {
  z-index: 1002 !important;
}

/* 모바일에서 터치 영역 최적화 */
.v-list-item {
  min-height: 56px;
  padding: 12px 16px;
}

/* 모바일 버튼 터치 영역 개선 */
.v-btn {
  min-height: 44px;
  touch-action: manipulation;
}

/* 모바일 검색창 최적화 */
.v-text-field {
  font-size: 16px; /* iOS에서 줌 방지 */
}
</style>
